import { ApiClientError, createApiClient } from '@igoal/api-client';
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiClientError && err.status < 500) && count < 2,
    },
  },
});

export const api = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? '',
  mode: 'cookie',
  onUnauthenticated: () => {
    queryClient.setQueryData(['me'], null);
  },
});

export function errorMessage(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

export function fieldErrors(err: unknown): Record<string, string> {
  return err instanceof ApiClientError ? err.fieldErrors : {};
}

/**
 * Open a PDF from the API in a new tab (the browser's viewer: print or save
 * from there). The tab is opened straight away so pop-up blockers allow it,
 * then filled once the file arrives; if it was blocked, the file downloads.
 */
export async function openPdf(p: Promise<{ blob: Blob; fileName: string }>) {
  const tab = window.open('', '_blank');
  try {
    const { blob, fileName } = await p;
    if (!tab || tab.closed) return saveDownload(Promise.resolve({ blob, fileName }));
    const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    tab.location.href = url;
    // keep the file long enough for the viewer to load and print it
    setTimeout(() => URL.revokeObjectURL(url), 10 * 60 * 1000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}

export async function saveDownload(p: Promise<{ blob: Blob; fileName: string }>) {
  const { blob, fileName } = await p;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
